import { Box, Button, Typography } from '@mui/material';
import Breadcrumbs from 'components/breadcrumbs';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import Paper from 'components/paper';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import _ from 'underscore';
import { useBluViuNutritionState } from '../context/bluviu-nutrition-context';
import { useSelectedSite } from '../hooks/use-selected-site';
import { BluViuNutritionParams } from '../types/bluviu-nutrition-params.type';
import { BluViuNutritionSite } from '../types/bluviu-nutrition-site.type';
import BluViuNutritionTabsHeader from './bluviu-nutrition-tabs-header';
import SelectSite from './select-site';
import Pagination from 'components/pagination';
import { usePagination } from 'hooks/use-pagination';

type Props = {};

const PAGINATION_LIMIT = 10;

const ListSites: React.FC<Props> = ({}) => {
  const history = useHistory();
  const params = useParams<BluViuNutritionParams>();
  const { currentPage, from, to, handlePagination } = usePagination(PAGINATION_LIMIT);
  const {
    bluViuNutritionSitesQuery: { data: sites, isLoading },
  } = useBluViuNutritionState();

  const [rows, setRows] = useState<BluViuNutritionSite[]>([]);

  useEffect(() => {
    if (!sites?.length) return;
    setRows(_.sortBy(sites, 'siteName'));
  }, [sites]);

  const handleClick = useCallback(
    (selectedSiteId: string) => {
      history.push(`/bluviu-nutrition/${params.tab}/${selectedSiteId}`);
    },
    [params.tab]
  );

  return (
    <Box display={'flex'} flexDirection={'column'}>
      <List
        isLoading={isLoading}
        noResult={{
          show: !sites?.length,
          text: 'No results',
        }}
        orderBy={'siteName'}
        rows={rows}
        sort={setRows}
        options={[
          { label: 'Site name', id: 'siteName' },
          { label: 'No. of scenarios', id: 'scenarioCount', align: 'right' },
          { label: 'No. of calibrations', id: 'calibrationCount', align: 'right' },
          { label: '', id: 'actions', align: 'right' },
        ]}>
        {rows?.slice(from, to).map((site, idx) => (
          <ListRow key={idx} onClick={() => handleClick(`${site.globalSiteId}`)}>
            <ListCell>{site.siteName}</ListCell>
            <ListCell align="right">{site.scenarioCount}</ListCell>
            <ListCell align="right">{site.calibrationCount}</ListCell>
            <ListCell align="right">
              <Button size="small" onClick={() => handleClick(site.globalSiteId)}>
                <FormattedMessage id={`general.view`} />
              </Button>
            </ListCell>
          </ListRow>
        ))}
      </List>
      <Pagination currentPage={currentPage} pages={Math.ceil(rows.length / PAGINATION_LIMIT)} onClick={handlePagination} />
    </Box>
  );
};

const SitesOverview: React.FC<Props> = ({}) => {
  const { selectedSiteId } = useSelectedSite();
  const params = useParams<BluViuNutritionParams>();
  const {
    bluViuNutritionSitesQuery: { data: sites, isLoading },
  } = useBluViuNutritionState();

  return (
    <>
      <BluViuNutritionTabsHeader
        intro={
          <Typography color="white">
            <FormattedMessage id={`general.confidential`} />
          </Typography>
        }
        utils={
          <Breadcrumbs
            crumbs={
              selectedSiteId
                ? [
                    {
                      text: sites?.find((s) => s.globalSiteId === params.globalSiteId)?.siteName || params.globalSiteId,
                      url: ``,
                    },
                  ]
                : undefined
            }
          />
        }
      />
      <Paper
        transparent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}>
        <Box display={'flex'} justifyContent={'flex-end'}>
          <SelectSite />
        </Box>
        <ListSites />
      </Paper>
    </>
  );
};

export default React.memo(SitesOverview);
